import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import PageTitle from 'components/page-title';
import { EmbeddedContext } from './embedded';

/**
 * Gộp vài trang "anh em" (vd. Đơn nghỉ phép + Loại nghỉ phép) thành MỘT trang có tab,
 * không phải viết lại trang cũ: mỗi tab render nguyên component trang cũ.
 *
 * - Tab đang mở nằm trên URL (`?tab=types`) → F5, nút Back, gửi link đều giữ đúng tab.
 * - Trang con biết mình đang nằm trong tab qua `useIsEmbedded()`; PageTitle của trang
 *   con tự ẩn tiêu đề để không bị hai đầu trang chồng nhau.
 * - Route cũ của trang con vẫn giữ (redirect về đây với `?tab=`), bookmark không gãy.
 */
export interface TabbedPageTab {
  key: string;
  label: React.ReactNode;
  render: () => React.ReactNode;
  /** Ẩn tab (vd. chỉ ADMIN mới dùng được). */
  hidden?: boolean;
}

interface Props {
  tabs: TabbedPageTab[];
  /** Tiêu đề khi trang không có trong menu; bình thường lấy tên menu. */
  title?: string;
  extra?: React.ReactNode;
}

export { useIsEmbedded } from './embedded';

const TabbedPage = ({ tabs, title = '', extra }: Props) => {
  const [params, setParams] = useSearchParams();
  const visible = tabs.filter((tab) => !tab.hidden);
  const requested = params.get('tab');
  const active = visible.find((tab) => tab.key === requested)?.key ?? visible[0]?.key;

  const onChange = (key: string) => {
    // Đổi tab thì bỏ bộ lọc của tab cũ, chỉ giữ tab.
    const next = new URLSearchParams();
    if (key !== visible[0]?.key) next.set('tab', key);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageTitle title={title} extra={extra} />
      <Tabs
        activeKey={active}
        onChange={onChange}
        destroyOnHidden
        more={{ icon: <EllipsisOutlined aria-label="Các thẻ khác" /> }}
        items={visible.map((tab) => ({
          key: tab.key,
          label: tab.label,
          children: <EmbeddedContext.Provider value>{tab.render()}</EmbeddedContext.Provider>,
        }))}
      />
    </div>
  );
};

export default TabbedPage;
